import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { request } from "../lib/api";
import type { AuditEntry, AuditLogResponse, UsersResponse } from "../types";
import { formatDateTime } from "../lib/datetime";
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Table,
  errorMessage,
  inputClass,
  labelClass,
  secondaryButtonClass,
} from "../components/ui";

const PAGE_SIZE = 50;

const actions = [
  "create",
  "update",
  "delete",
  "archive",
  "duplicate",
  "reorder",
  "move",
  "translate",
  "bulk_import",
  "export",
  "denied",
];

const roleLabels: Record<string, string> = {
  super_admin: "Super admin",
  admin: "Admin",
  facilitator: "Facilitator",
  teacher: "Teacher",
};

const roleName = (role: string | null | undefined) => (role ? (roleLabels[role] ?? role) : "—");

interface Account {
  id: string;
  name: string;
  role: string;
}

// What every admin account has done, and what it tried to do and was refused.
// Super Admin only: this is how work by lower-authority accounts is checked.
export function ActivityLog() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      request<UsersResponse>("/api/admin/users?role=admin&limit=200"),
      request<UsersResponse>("/api/admin/users?role=super_admin&limit=20"),
    ])
      .then(([admins, superAdmins]) =>
        setAccounts(
          [...superAdmins.users, ...admins.users].flatMap((user) =>
            user.id ? [{ id: user.id, name: user.name || user.assigned_id, role: user.role }] : []
          )
        )
      )
      .catch(() => setAccounts([]));
  }, []);

  const fetchPage = useCallback(
    (offset: number) => {
      const query = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (actor) query.set("actor", actor);
      if (action) query.set("action", action);
      return request<AuditLogResponse>(`/api/admin/audit?${query}`);
    },
    [actor, action]
  );

  useEffect(() => {
    let cancelled = false;
    fetchPage(0)
      .then((data) => {
        if (cancelled) return;
        setEntries(data.entries);
        setHasMore(data.entries.length === PAGE_SIZE);
      })
      .catch((cause) => {
        if (!cancelled) setError(errorMessage(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  // A new filter starts the list over; clearing here, not in the effect, keeps
  // the old rows from showing under the new filter while it loads.
  function changeFilter(set: (value: string) => void, value: string) {
    set(value);
    setEntries(null);
    setError("");
  }

  async function loadMore() {
    if (!entries) return;
    setLoadingMore(true);
    try {
      const data = await fetchPage(entries.length);
      setEntries([...entries, ...data.entries]);
      setHasMore(data.entries.length === PAGE_SIZE);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <Card
      title="Activity log"
      text="Every change made through the admin portal, who made it, and the role they held at the time. Refused requests and data exports are included."
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <label className={labelClass}>
          Account
          <select
            className={inputClass}
            value={actor}
            onChange={(event) => changeFilter(setActor, event.target.value)}
          >
            <option value="">All admin accounts</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} ({roleName(account.role)})
              </option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          Action
          <select
            className={inputClass}
            value={action}
            onChange={(event) => changeFilter(setAction, event.target.value)}
          >
            <option value="">All actions</option>
            {actions.map((name) => (
              <option key={name} value={name}>
                {name === "denied" ? "denied (refused request)" : name.replace("_", " ")}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? (
        <ErrorState message={error} />
      ) : entries === null ? (
        <LoadingState message="Loading activity…" />
      ) : !entries.length ? (
        <EmptyState title="No activity" message="Nothing matches these filters yet." />
      ) : (
        <>
          <Table
            headers={[
              { label: "When" },
              { label: "Who" },
              { label: "Action" },
              { label: "What" },
              { label: "Details" },
            ]}
            rows={entries.map((entry) => [
              <span key="when" className="whitespace-nowrap">
                {formatDateTime(entry.occurred_at)}
              </span>,
              <span key="who">
                <strong className="block font-semibold text-content-primary">
                  {entry.actor.name}
                </strong>
                <span className="block text-xs text-content-secondary">
                  {roleName(entry.actor_role ?? entry.actor.role)}
                  {entry.actor_role && entry.actor_role !== entry.actor.role
                    ? ` then, ${roleName(entry.actor.role)} now`
                    : ""}
                  {!entry.actor.active && " · deactivated"}
                </span>
              </span>,
              <Badge key="action" tone={entry.action === "denied" ? "danger" : "neutral"}>
                {entry.action.replace("_", " ")}
              </Badge>,
              <span key="what">
                {entry.entity.replace(/_/g, " ")}
                <code className="block max-w-[14rem] truncate text-xs text-content-secondary">
                  {entry.entity_id}
                </code>
              </span>,
              <ChangeDetails key="details" entry={entry} />,
            ])}
          />
          {hasMore && (
            <button
              type="button"
              className={`${secondaryButtonClass} mt-4`}
              onClick={() => void loadMore()}
              disabled={loadingMore}
            >
              {loadingMore && <Loader2 size={16} className="animate-spin" />}
              Load more
            </button>
          )}
        </>
      )}
    </Card>
  );
}

function ChangeDetails({ entry }: { entry: AuditEntry }) {
  if (entry.before == null && entry.after == null) {
    return <span className="text-content-disabled">—</span>;
  }
  return (
    <details className="max-w-md">
      <summary className="cursor-pointer text-xs font-semibold text-interactive">View</summary>
      {entry.before != null && (
        <>
          <p className="mt-2 text-xs font-semibold text-content-secondary">Before</p>
          <pre className="max-h-48 overflow-auto rounded bg-surface-muted p-2 text-xs">
            {JSON.stringify(entry.before, null, 2)}
          </pre>
        </>
      )}
      {entry.after != null && (
        <>
          <p className="mt-2 text-xs font-semibold text-content-secondary">After</p>
          <pre className="max-h-48 overflow-auto rounded bg-surface-muted p-2 text-xs">
            {JSON.stringify(entry.after, null, 2)}
          </pre>
        </>
      )}
    </details>
  );
}
